import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Headphones, Pause, Play, ChevronDown, ShoppingBag } from "lucide-react";
import { resolveImage, API_BASE } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { computeSalePrice, useSiteSpecial } from "@/lib/pricingHelpers";
import SalePrice from "@/components/SalePrice";
import { toast } from "sonner";

/**
 * Premium "Story Experience" — full-bleed immersive image at the top,
 * then a torn-paper journal card emerges with parallax + fade-up reveal,
 * carrying the artwork's story like a personal note. Ends with the
 * interactive variant picker + CTA — single source of truth for purchase.
 */
export default function StoryExperience({ artwork, qrCode }) {
  // Hooks must run unconditionally. Compute values safely from a possibly-null artwork.
  const a = artwork || {};
  const story = a.story || "";
  const tagline = (a.short_description || "").trim();
  const num = String(a.collection_number || "—").padStart(2, "0");
  const settings = useSiteSettings();
  const collectionSize = settings.collection_goal || 52;
  const tags = (a.tags && a.tags.length)
    ? a.tags
    : ["Hope", "Strength", "Resilience", "Growth", "Faith", "Perspective"];

  const audioSrc = a.audio_url
    ? (a.audio_url.startsWith("/api/") ? `${API_BASE}${a.audio_url}` : a.audio_url)
    : null;

  // === Purchase state (moved up from ArtworkDetail) ===
  const variants = (a.variants || []).filter(v => v.type !== "upgrade" && v.active);
  const upgrade = (a.variants || []).find(v => v.type === "upgrade" && v.active);
  const defaultKey = variants[0]?.key || "";
  const [variantKey, setVariantKey] = useState(defaultKey);
  const [personalText, setPersonalText] = useState("");
  const [addUpgrade, setAddUpgrade] = useState(false);
  const { addItem } = useCart();
  const nav = useNavigate();

  const chosen = variants.find(v => v.key === variantKey) || variants[0];
  const upgradeCost = addUpgrade && upgrade ? upgrade.price : 0;
  const unit = (chosen?.price || 0) + upgradeCost;
  // Sale-aware total
  const siteSpecial = useSiteSpecial();
  const chosenSale = computeSalePrice(chosen?.price || 0, a, siteSpecial);
  const effectiveUnit = chosenSale.effective + upgradeCost;
  const originalUnit = chosenSale.original + upgradeCost;
  const totalOnSale = chosenSale.isOnSale && effectiveUnit < originalUnit;

  const handleAdd = (goToCheckout = false) => {
    if (a.locked) { nav("/subscriptions"); return; }
    if (!chosen) return;
    addItem({
      artwork_id: a.id, artwork_slug: a.slug, title: a.title,
      main_image: a.main_image, variant_key: chosen.key, variant_label: chosen.label,
      variant_type: chosen.type, price: chosen.price, quantity: 1,
      personal_text: addUpgrade && personalText ? personalText : null,
      qr_code: qrCode || null,
    });
    if (addUpgrade && upgrade) {
      addItem({
        artwork_id: a.id, artwork_slug: a.slug, title: `${a.title} — Personal Note`,
        main_image: a.main_image, variant_key: upgrade.key, variant_label: upgrade.label,
        variant_type: "upgrade", price: upgrade.price, quantity: 1,
        personal_text: personalText || null, qr_code: qrCode || null,
      });
    }
    toast.success("Added to your cart.");
    if (goToCheckout) nav("/cart");
  };

  // === Immersive hero image with parallax ===
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  // === Story card reveal ===
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { once: true, margin: "-15% 0px -10% 0px" });

  // === Audio player ===
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const toggle = () => {
    const el = audioRef.current; if (!el) return;
    if (el.paused) { const p = el.play(); if (p?.catch) p.then(() => setPlaying(true)).catch(() => setPlaying(false)); else setPlaying(true); }
    else { el.pause(); setPlaying(false); }
  };

  if (!artwork) return null;

  return (
    <section className="bg-[#0A0A0A] text-[#F5F2EB] relative overflow-hidden" data-testid="story-experience">
      {/* ============ Immersive Hero ============ */}
      <div ref={heroRef} className="relative min-h-[88vh] flex items-center justify-center px-6 md:px-10">
        <motion.div
          className="relative w-full max-w-[1100px] aspect-[4/5] md:aspect-[16/10] mx-auto"
          style={{ y: imgY, scale: imgScale }}
        >
          <img
            src={resolveImage(a.main_image)}
            alt={a.title}
            className="absolute inset-0 w-full h-full object-contain"
            data-testid="story-hero-image"
          />
        </motion.div>

        {/* Overlay caption */}
        <motion.div
          className="absolute bottom-10 left-0 right-0 text-center pointer-events-none px-6"
          style={{ opacity: titleOpacity }}
        >
          <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A46A] mb-2">Truth #{num} of {collectionSize}</div>
          <div className="mt-6 inline-flex flex-col items-center text-[#7a766f] animate-bounce">
            <ChevronDown size={20}/>
            <div className="font-ui text-[9px] tracking-[0.32em] uppercase mt-1">Scroll for the truth</div>
          </div>
        </motion.div>
      </div>

      {/* ============ Journal Card ============ */}
      <div className="relative px-6 md:px-10 pb-32" ref={cardRef}>
        <div className="relative max-w-[760px] mx-auto -mt-10 md:-mt-20">
          {/* Soft shadow behind paper */}
          <div className="absolute inset-x-6 -bottom-6 h-12 bg-black/70 blur-3xl rounded-full" aria-hidden/>
          <motion.article
            className="story-paper relative"
            initial={{ opacity: 0, y: 80, rotate: -0.4 }}
            animate={inView ? { opacity: 1, y: 0, rotate: -0.4 } : {}}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            data-testid="story-card"
          >
            <div className="px-7 md:px-14 py-12 md:py-16">
              <div className="text-center">
                <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#9b7d3c] mb-3">Truth #{num} of {collectionSize}</div>
                <h3 className="font-display text-3xl md:text-5xl text-[#2b2620] tracking-tight" data-testid="story-card-title">{(a.title || "").toUpperCase()}</h3>
                {tagline && (
                  <p className="font-script text-2xl md:text-3xl text-[#9b7d3c] mt-3 italic">&ldquo;{tagline.replace(/^"|"$/g, "")}&rdquo;</p>
                )}
              </div>

              <div className="my-8 flex items-center gap-4 text-[#9b7d3c]" aria-hidden>
                <div className="flex-1 h-px bg-[#c9b78a]"/>
                <div className="font-ui text-[10px] tracking-[0.42em] uppercase">The Truth Behind This Piece</div>
                <div className="flex-1 h-px bg-[#c9b78a]"/>
              </div>

              {audioSrc && (
                <div className="text-center mb-8">
                  <button
                    type="button"
                    onClick={toggle}
                    className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-[#9b7d3c] text-[#5a4a24] hover:bg-[#9b7d3c] hover:text-[#fdf7e8] font-ui text-[11px] tracking-[0.32em] uppercase transition-colors"
                    data-testid="story-listen-btn"
                  >
                    {playing ? <Pause size={14}/> : <Headphones size={14}/>}
                    {playing ? "Pause" : "Listen to the Story"}
                  </button>
                  {progress > 0 && (
                    <div className="mt-3 mx-auto max-w-xs h-[2px] bg-[#d9c9a3]">
                      <div className="h-full bg-[#9b7d3c]" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                  <audio
                    ref={audioRef}
                    src={audioSrc}
                    preload="none"
                    onTimeUpdate={e => {
                      const el = e.currentTarget;
                      if (el.duration > 0) setProgress((el.currentTime / el.duration) * 100);
                    }}
                    onEnded={() => { setPlaying(false); setProgress(100); }}
                  />
                </div>
              )}

              {story ? (
                <div className="story-body font-body text-[17px] md:text-[18px] leading-[1.95] text-[#3a342c] whitespace-pre-line" data-testid="story-body">
                  {story}
                </div>
              ) : (
                <p className="font-body italic text-center text-[#7a6f55]">No story written yet for this piece.</p>
              )}

              <div className="mt-12 text-right">
                <div className="font-script text-3xl md:text-4xl text-[#5a4a24] -rotate-2 inline-block" data-testid="story-signature">— Mickey Trivett</div>
              </div>
            </div>
          </motion.article>
        </div>

        {/* ============ Tags ============ */}
        <motion.div
          className="max-w-[760px] mx-auto mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.3 }}
          data-testid="story-tags"
        >
          <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A46A] mb-5">What this piece represents</div>
          <div className="flex flex-wrap justify-center gap-2.5">
            {tags.map(t => (
              <span key={t} className="px-4 py-1.5 border border-[#C9A46A]/40 text-[#E8E0CC] font-ui text-[11px] tracking-[0.18em] uppercase hover:border-[#C9A46A] hover:text-[#C9A46A] transition-colors" data-testid={`story-tag-${t}`}>
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* ============ Own This Truth — interactive purchase ============ */}
        <div className="max-w-[760px] mx-auto mt-20 border-t border-white/10 pt-14" id="own-this-piece" data-testid="own-this-piece">
          <div className="text-center mb-10">
            <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A46A] mb-3">Own This Truth</div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#F5F2EB]">Carry it with you.</h3>
            <p className="font-body text-[15px] text-[#9a9690] mt-3 max-w-md mx-auto">Choose how this piece lives in your world — printed, framed, or signed by Mickey himself.</p>
          </div>

          {a.locked ? (
            <div className="space-y-4">
              <div className="border border-[#C9A055]/40 bg-[#C9A055]/10 p-5 text-center" data-testid="locked-banner">
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1">Members Only</div>
                <p className="font-body text-[#F4F1EA] text-sm">This truth is available to Simple Truths Club members.</p>
              </div>
              <button data-testid="unlock-cta" onClick={() => nav("/subscriptions")} className="btn-gold w-full">Join the Club to Unlock</button>
            </div>
          ) : (
            <>
              {/* Variants */}
              <div className="border border-white/10 divide-y divide-white/10" data-testid="variant-picker">
                {variants.map(v => (
                  <label key={v.key} data-testid={`variant-${v.key}`} className={`flex items-center justify-between gap-4 px-5 py-4 cursor-pointer hover:bg-white/5 transition-colors ${variantKey === v.key ? 'bg-[#C9A46A]/10' : ''}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${variantKey === v.key ? "border-[#C9A46A]" : "border-white/30"}`}>
                        {variantKey === v.key && <span className="w-2 h-2 rounded-full bg-[#C9A46A]"/>}
                      </span>
                      <input type="radio" className="sr-only" checked={variantKey === v.key} onChange={() => setVariantKey(v.key)} />
                      <span className="font-ui-tight text-[15px] text-[#F2EFE8]">{v.label}</span>
                    </div>
                    <SalePrice price={v.price} artwork={a} size="sm" compact />
                  </label>
                ))}
              </div>

              {/* Upgrade */}
              {upgrade && (
                <div className="mt-3">
                  <label className={`flex items-start gap-4 px-5 py-4 border cursor-pointer ${addUpgrade ? "border-[#C9A46A] bg-[#C9A46A]/10" : "border-white/10 hover:bg-white/5"}`}>
                    <input type="checkbox" data-testid="upgrade-toggle" checked={addUpgrade} onChange={e => setAddUpgrade(e.target.checked)} className="mt-1 accent-[#C9A46A]"/>
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline">
                        <span className="font-ui-tight text-[15px] text-[#F2EFE8]">Personal Text Upgrade</span>
                        <span className="font-ui-tight text-[15px] text-[#C9A46A]">+${upgrade.price.toFixed(2)}</span>
                      </div>
                      <div className="font-body text-[13px] text-[#9a9690] mt-1">Add a personal message from Mickey Trivett with your order.</div>
                      {addUpgrade && (
                        <textarea
                          data-testid="personal-text-input"
                          value={personalText} onChange={e => setPersonalText(e.target.value)}
                          placeholder="What should Mickey know? (Optional)"
                          rows={3}
                          className="stc-input mt-3 font-body"
                        />
                      )}
                    </div>
                  </label>
                </div>
              )}

              {/* Total + CTAs */}
              <div className="mt-6 flex items-baseline justify-between border-t border-white/10 pt-5">
                <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#9a9690]">Your Total</div>
                <div className="font-display-saira text-3xl text-[#C9A46A]" data-testid="story-total-price">
                  {totalOnSale ? (
                    <span className="stc-sale-price inline-flex items-baseline gap-2">
                      <span className="stc-sale-strike font-ui-tight text-xl text-[#6a6660] line-through decoration-[#d97a7a] decoration-[1.5px]">${originalUnit.toFixed(2)}</span>
                      <span className="stc-sale-effective text-3xl">${effectiveUnit.toFixed(2)}</span>
                    </span>
                  ) : `$${unit.toFixed(2)}`}
                </div>
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <button data-testid="add-to-cart-btn" onClick={() => handleAdd(false)} className="btn-gold w-full"><ShoppingBag size={16}/> Add to Cart</button>
                <button data-testid="buy-now-btn" onClick={() => handleAdd(true)} className="btn-outline w-full">Buy Now</button>
              </div>
            </>
          )}

          {/* Piece Details */}
          <div className="mt-14 grid sm:grid-cols-2 gap-8" data-testid="story-details">
            <div>
              <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A46A] mb-4">Piece Details</div>
              <dl className="space-y-2 font-body text-[14px]">
                <div className="flex justify-between border-b border-white/5 pb-2">
                  <dt className="text-[#8a857d]">Piece</dt>
                  <dd className="text-[#F5F2EB]">#{num} of {collectionSize}</dd>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-2">
                  <dt className="text-[#8a857d]">Collection</dt>
                  <dd className="text-[#F5F2EB]">Simple Truths Collection</dd>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-2">
                  <dt className="text-[#8a857d]">Artist</dt>
                  <dd className="text-[#F5F2EB]">Mickey Trivett</dd>
                </div>
              </dl>
            </div>
            <div>
              <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A46A] mb-4">Shipping</div>
              <ul className="space-y-2 font-body text-[14px] text-[#E8E0CC]">
                <li className="flex justify-between border-b border-white/5 pb-2">
                  <span className="text-[#8a857d]">Processing</span>
                  <span>3–5 business days</span>
                </li>
                {chosen?.weight ? (
                  <li className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-[#8a857d]">Weight</span>
                    <span>{(chosen.weight / 16).toFixed(1)} lbs</span>
                  </li>
                ) : null}
                <li className="flex justify-between border-b border-white/5 pb-2">
                  <span className="text-[#8a857d]">Signed</span>
                  <span>By the artist</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
